import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Plus, Edit2, Trash2, Eye, Calendar, Star, ListChecks, 
  MessageSquare, ToggleLeft, ToggleRight, GripVertical, Save, X,
  FolderOpen, ChevronDown, ChevronRight, Pencil, Check, CheckSquare, Eraser, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { AdminLayout } from './AdminDashboard';
import { surveyAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';

const SurveyBuilder = () => {
  const { isSuperAdmin, user } = useAuth();
  const isReadOnly = user?.role === 'sr_management';
  const [surveys, setSurveys] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showSurveyDialog, setShowSurveyDialog] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [surveySections, setSurveySections] = useState(['General']);
  const [newSectionName, setNewSectionName] = useState('');
  const [showSectionManager, setShowSectionManager] = useState(false);
  const [sectionLabels, setSectionLabels] = useState({});
  const [editingLabelsFor, setEditingLabelsFor] = useState(null);
  const [sectionWeights, setSectionWeights] = useState({});
  const [sectionGroups, setSectionGroups] = useState([]); // [{name, sections:[]}]
  const [editingSectionName, setEditingSectionName] = useState(null);
  const [editedSectionText, setEditedSectionText] = useState('');
  const [showClearResponsesDialog, setShowClearResponsesDialog] = useState(false);
  const [clearConfirmText, setClearConfirmText] = useState('');
  const [clearingResponses, setClearingResponses] = useState(false);

  const DEFAULT_SCALE_PRESETS = [
    { name: 'Satisfaction', labels: { '1': 'Very Dissatisfied', '2': 'Dissatisfied', '3': 'Neutral', '4': 'Satisfied', '5': 'Very Satisfied' } },
    { name: 'Agreement', labels: { '1': 'Strongly Disagree', '2': 'Disagree', '3': 'Neutral', '4': 'Agree', '5': 'Strongly Agree' } },
    { name: 'Frequency', labels: { '1': 'Never', '2': 'Rarely', '3': 'Sometimes', '4': 'Often', '5': 'Always' } },
    { name: 'Likelihood', labels: { '1': 'Very Unlikely', '2': 'Unlikely', '3': 'Neutral', '4': 'Likely', '5': 'Very Likely' } },
  ];
  
  const [surveyForm, setSurveyForm] = useState({
    title: '',
    description: '',
    start_date: new Date(),
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    is_anonymous: true,
    questions: []
  });

  const [questionForm, setQuestionForm] = useState({
    text: '',
    type: 'rating',
    is_required: true,
    options: [],
    section: 'General',
    min_selections: null,
    max_selections: null
  });

  useEffect(() => {
    loadSurveys();
  }, []);

  const loadSurveys = async () => {
    try {
      const response = await surveyAPI.getAll();
      setSurveys(response.data);
    } catch (error) {
      toast.error('Failed to load surveys');
    } finally {
      setLoading(false);
    }
  };

  const resetSurveyForm = () => {
    setSurveyForm({
      title: '',
      description: '',
      start_date: new Date(),
      end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      is_anonymous: true,
      questions: []
    });
    setEditingSurvey(null);
  };

  const resetQuestionForm = () => {
    setQuestionForm({
      text: '',
      type: 'rating',
      is_required: true,
      options: [],
      section: 'General',
      min_selections: null,
      max_selections: null
    });
    setEditingQuestion(null);
  };

  const handleCreateSurvey = () => {
    resetSurveyForm();
    setShowSurveyDialog(true);
  };

  const handleEditSurvey = (survey) => {
    setSurveyForm({
      title: survey.title,
      description: survey.description,
      start_date: new Date(survey.start_date),
      end_date: new Date(survey.end_date),
      is_anonymous: survey.is_anonymous,
      questions: survey.questions || []
    });
    setEditingSurvey(survey);
    setShowSurveyDialog(true);
  };

  const handleSaveSurvey = async () => {
    if (!surveyForm.title || !surveyForm.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      // Normalize start to 00:00:00 local, end to 23:59:59 local so admin's "May 29" 
      // covers the entire day in their timezone
      const startLocal = new Date(surveyForm.start_date);
      startLocal.setHours(0, 0, 0, 0);
      const endLocal = new Date(surveyForm.end_date);
      endLocal.setHours(23, 59, 59, 999);

      if (editingSurvey) {
        await surveyAPI.update(editingSurvey.id, {
          title: surveyForm.title,
          description: surveyForm.description,
          start_date: startLocal.toISOString(),
          end_date: endLocal.toISOString(),
          is_anonymous: surveyForm.is_anonymous
        });
        toast.success('Survey updated successfully');
      } else {
        await surveyAPI.create({
          ...surveyForm,
          start_date: startLocal.toISOString(),
          end_date: endLocal.toISOString()
        });
        toast.success('Survey created successfully');
      }
      setShowSurveyDialog(false);
      resetSurveyForm();
      loadSurveys();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save survey');
    }
  };

  const handleDeleteSurvey = async (surveyId) => {
    if (!window.confirm('Are you sure you want to delete this survey? Only Super Admin can view deleted surveys.')) return;
    
    try {
      await surveyAPI.delete(surveyId);
      toast.success('Survey deleted');
      loadSurveys();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete survey');
    }
  };

  const handleToggleActive = async (survey) => {
    try {
      if (survey.is_active) {
        await surveyAPI.deactivate(survey.id);
        toast.success('Survey deactivated');
      } else {
        await surveyAPI.update(survey.id, { is_active: true });
        toast.success('Survey activated');
      }
      loadSurveys();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update survey');
    }
  };

  const handleReactivateSurvey = async (surveyId) => {
    try {
      await surveyAPI.reactivate(surveyId);
      toast.success('Survey reactivated');
      loadSurveys();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate survey');
    }
  };

  const handleViewSurvey = (survey) => {
    setSelectedSurvey(survey);
    const existingSections = [...new Set((survey.questions || []).map(q => q.section || 'General'))];
    if (!existingSections.includes('General')) {
      existingSections.push('General');
    }
    setSurveySections(existingSections);
    setSectionLabels(survey.section_labels || {});
    setSectionWeights(survey.section_weights || {});
    setSectionGroups(survey.section_groups || []);
  };

  const handleSaveSectionGroups = async () => {
    if (!selectedSurvey) return;
    try {
      const clean = sectionGroups
        .map((g) => ({ name: (g.name || '').trim(), sections: (g.sections || []).filter((s) => s) }))
        .filter((g) => g.name && g.sections.length > 0);
      await surveyAPI.updateSectionGroups(selectedSurvey.id, clean);
      toast.success('Composite groups saved');
      const refreshed = await surveyAPI.getById(selectedSurvey.id);
      setSelectedSurvey(refreshed.data);
      setSectionGroups(refreshed.data.section_groups || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save groups');
    }
  };

  const addGroup = () => setSectionGroups([...sectionGroups, { name: `Group ${sectionGroups.length + 1}`, sections: [] }]);
  const removeGroup = (idx) => setSectionGroups(sectionGroups.filter((_, i) => i !== idx));
  const updateGroupName = (idx, name) => setSectionGroups(sectionGroups.map((g, i) => i === idx ? { ...g, name } : g));
  const toggleGroupSection = (idx, section) => {
    setSectionGroups(sectionGroups.map((g, i) => {
      if (i !== idx) return g;
      const has = (g.sections || []).includes(section);
      return { ...g, sections: has ? g.sections.filter((s) => s !== section) : [...(g.sections || []), section] };
    }));
  };

  const handleSaveSectionWeights = async () => {
    if (!selectedSurvey) return;
    try {
      const clean = {};
      Object.entries(sectionWeights).forEach(([k, v]) => {
        const n = Number(v) || 0;
        if (n > 0) clean[k] = n;
      });
      await surveyAPI.updateSectionWeights(selectedSurvey.id, clean);
      toast.success('Section weights saved');
      const refreshed = await surveyAPI.getById(selectedSurvey.id);
      setSelectedSurvey(refreshed.data);
      setSectionWeights(refreshed.data.section_weights || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save weights');
    }
  };

  const handleClearAllResponses = async () => {
    if (!selectedSurvey) return;
    if (clearConfirmText.trim() !== selectedSurvey.title) {
      toast.error('Type the survey title exactly to confirm');
      return;
    }
    setClearingResponses(true);
    try {
      const res = await surveyAPI.clearResponses(selectedSurvey.id);
      toast.success(res.data.message || 'Responses cleared');
      setShowClearResponsesDialog(false);
      setClearConfirmText('');
      // Refresh survey
      const refreshed = await surveyAPI.getById(selectedSurvey.id);
      setSelectedSurvey(refreshed.data);
      loadSurveys();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear responses');
    } finally {
      setClearingResponses(false);
    }
  };

  const handleAddQuestion = () => {
    resetQuestionForm();
    setShowQuestionDialog(true);
  };

  const handleAddSection = () => {
    if (newSectionName.trim() && !surveySections.includes(newSectionName.trim())) {
      setSurveySections([...surveySections, newSectionName.trim()]);
      setQuestionForm({ ...questionForm, section: newSectionName.trim() });
      setNewSectionName('');
      toast.success(`Section "${newSectionName.trim()}" added`);
    }
  };

  const handleSaveSectionLabels = async (sectionName, labels) => {
    const updatedLabels = { ...sectionLabels, [sectionName]: labels };
    setSectionLabels(updatedLabels);
    if (selectedSurvey) {
      try {
        await surveyAPI.updateSectionLabels(selectedSurvey.id, updatedLabels);
        toast.success(`Scale labels saved for "${sectionName}"`);
      } catch (error) {
        toast.error('Failed to save labels');
      }
    }
    setEditingLabelsFor(null);
  };

  const handleApplyPreset = (sectionName, preset) => {
    handleSaveSectionLabels(sectionName, { ...preset.labels });
  };

  const handleRenameSection = async (oldName, newName) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName) {
      setEditingSectionName(null);
      return;
    }
    if (surveySections.includes(trimmed)) {
      toast.error(`Section "${trimmed}" already exists`);
      return;
    }

    // Update sections list
    const updatedSections = surveySections.map(s => s === oldName ? trimmed : s);
    setSurveySections(updatedSections);

    // Move labels from old name to new name
    const updatedLabels = { ...sectionLabels };
    if (updatedLabels[oldName]) {
      updatedLabels[trimmed] = updatedLabels[oldName];
      delete updatedLabels[oldName];
      setSectionLabels(updatedLabels);
    }

    // Update questions in the survey (rename section in all questions)
    if (selectedSurvey) {
      try {
        const updatedQuestions = (selectedSurvey.questions || []).map(q =>
          q.section === oldName ? { ...q, section: trimmed } : q
        );
        await surveyAPI.update(selectedSurvey.id, { section_labels: updatedLabels });
        // Update questions section references via individual question updates
        for (const q of updatedQuestions.filter(q => q.section === trimmed)) {
          await surveyAPI.updateQuestion(selectedSurvey.id, q.id, { section: trimmed });
        }
        setSelectedSurvey({ ...selectedSurvey, questions: updatedQuestions, section_labels: updatedLabels });
        toast.success(`Section renamed to "${trimmed}"`);
      } catch (error) {
        toast.error('Failed to rename section');
      }
    }
    setEditingSectionName(null);
  };

  const handleDeleteSection = async (sectionName) => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can delete sections');
      return;
    }
    
    if (sectionName === 'General') {
      toast.error('Cannot delete the General section');
      return;
    }
    
    // Check if section has questions
    const questionsInSection = (selectedSurvey?.questions || []).filter(q => q.section === sectionName);
    
    if (questionsInSection.length > 0) {
      if (!window.confirm(`This section has ${questionsInSection.length} question(s). Delete section and move questions to "General"?`)) {
        return;
      }
      
      // Move all questions to General section
      try {
        for (const q of questionsInSection) {
          await surveyAPI.updateQuestion(selectedSurvey.id, q.id, { section: 'General' });
        }
        toast.success(`${questionsInSection.length} question(s) moved to General section`);
      } catch (error) {
        toast.error('Failed to move questions');
        return;
      }
    }
    
    // Remove section from list
    setSurveySections(surveySections.filter(s => s !== sectionName));
    toast.success(`Section "${sectionName}" deleted`);
    
    // Refresh survey data
    const response = await surveyAPI.getAll();
    const updatedSurvey = response.data.find(s => s.id === selectedSurvey.id);
    if (updatedSurvey) {
      setSelectedSurvey(updatedSurvey);
    }
  };

  const handleEditQuestion = (question) => {
    setQuestionForm({
      text: question.text,
      type: question.type,
      is_required: question.is_required,
      options: question.options || [],
      section: question.section || 'General',
      min_selections: question.min_selections ?? null,
      max_selections: question.max_selections ?? null
    });
    setEditingQuestion(question);
    setShowQuestionDialog(true);
  };

  const handleSaveQuestion = async () => {
    if (!questionForm.text) {
      toast.error('Question text is required');
      return;
    }

    if (questionForm.type === 'multiple_choice' && questionForm.options.length < 2) {
      toast.error('Multiple choice questions need at least 2 options');
      return;
    }

    if (questionForm.type === 'multi_select') {
      if (questionForm.options.length < 2) {
        toast.error('Multi-select questions need at least 2 options');
        return;
      }
      if (questionForm.max_selections && questionForm.max_selections > questionForm.options.length) {
        toast.error('Max selections cannot exceed number of options');
        return;
      }
      if (questionForm.min_selections && questionForm.max_selections && questionForm.min_selections > questionForm.max_selections) {
        toast.error('Min selections cannot exceed max selections');
        return;
      }
    }

    try {
      if (editingQuestion) {
        await surveyAPI.updateQuestion(selectedSurvey.id, editingQuestion.id, questionForm);
        toast.success('Question updated');
      } else {
        await surveyAPI.addQuestion(selectedSurvey.id, questionForm);
        toast.success('Question added');
      }
      setShowQuestionDialog(false);
      resetQuestionForm();
      
      // Refresh survey details
      const response = await surveyAPI.getById(selectedSurvey.id);
      setSelectedSurvey(response.data);
      loadSurveys();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save question');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Delete this question?')) return;
    
    try {
      await surveyAPI.deleteQuestion(selectedSurvey.id, questionId);
      toast.success('Question deleted');
      
      const response = await surveyAPI.getById(selectedSurvey.id);
      setSelectedSurvey(response.data);
      loadSurveys();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete question');
    }
  };

  const addOption = () => {
    setQuestionForm({
      ...questionForm,
      options: [...questionForm.options, { text: '', value: questionForm.options.length + 1, score: 0 }]
    });
  };

  const updateOption = (index, text) => {
    const newOptions = [...questionForm.options];
    newOptions[index] = { ...newOptions[index], text };
    setQuestionForm({ ...questionForm, options: newOptions });
  };

  const updateOptionScore = (index, score) => {
    const newOptions = [...questionForm.options];
    newOptions[index] = { ...newOptions[index], score: Number(score) || 0 };
    setQuestionForm({ ...questionForm, options: newOptions });
  };

  const removeOption = (index) => {
    setQuestionForm({
      ...questionForm,
      options: questionForm.options.filter((_, i) => i !== index)
    });
  };

  const getQuestionTypeIcon = (type) => {
    switch (type) {
      case 'rating': return <Star className="h-4 w-4" />;
      case 'yes_no': return <ToggleLeft className="h-4 w-4" />;
      case 'multiple_choice': return <ListChecks className="h-4 w-4" />;
      case 'multi_select': return <CheckSquare className="h-4 w-4" />;
      case 'open_ended': return <MessageSquare className="h-4 w-4" />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8">
        {selectedSurvey ? (
          // Survey Detail View
          <div>
            <div className="flex items-center justify-between mb-8">
              <div>
                <Button 
                  variant="ghost" 
                  onClick={() => setSelectedSurvey(null)}
                  className="mb-2"
                >
                  ← Back to Surveys
                </Button>
                <h1 className="font-heading text-3xl font-bold">{selectedSurvey.title}</h1>
                <p className="text-muted-foreground">{selectedSurvey.description}</p>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant={selectedSurvey.is_active ? 'default' : 'secondary'}>
                  {selectedSurvey.is_active ? 'Active' : 'Inactive'}
                </Badge>
                {isSuperAdmin && selectedSurvey.response_count > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => setShowClearResponsesDialog(true)}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                    data-testid="clear-responses-btn"
                  >
                    <Eraser className="h-4 w-4 mr-2" />
                    Clear All Responses
                  </Button>
                )}
                {selectedSurvey.response_count === 0 && (
                  <Button onClick={handleAddQuestion} data-testid="add-question-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Question
                  </Button>
                )}
              </div>
            </div>

            {/* Survey Info */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Start Date</p>
                    <p className="font-medium">{format(new Date(selectedSurvey.start_date), 'MMM dd, yyyy')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">End Date</p>
                    <p className="font-medium">{format(new Date(selectedSurvey.end_date), 'MMM dd, yyyy')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Responses</p>
                    <p className="font-medium">{selectedSurvey.response_count}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Anonymous</p>
                    <p className="font-medium">{selectedSurvey.is_anonymous ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Questions List - Grouped by Sections */}
            <Card data-testid="questions-list">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading">Questions ({selectedSurvey.questions?.length || 0})</CardTitle>
                {isSuperAdmin && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowSectionManager(true)}
                    data-testid="manage-sections-btn"
                  >
                    <FolderOpen className="h-4 w-4 mr-2" />
                    Manage Sections
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {selectedSurvey.questions?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No questions yet. Add your first question to get started.
                  </div>
                ) : (
                  // Group questions by section
                  (() => {
                    const groupedQuestions = (selectedSurvey.questions || []).reduce((acc, q) => {
                      const section = q.section || 'General';
                      if (!acc[section]) acc[section] = [];
                      acc[section].push(q);
                      return acc;
                    }, {});
                    
                    const sections = Object.keys(groupedQuestions);
                    
                    return sections.map((section, sectionIndex) => (
                      <Collapsible 
                        key={section}
                        open={expandedSections[section] !== false}
                        onOpenChange={(open) => setExpandedSections(prev => ({ ...prev, [section]: open }))}
                        className="border rounded-xl overflow-hidden"
                      >
                        <CollapsibleTrigger className="w-full">
                          <div className="flex items-center justify-between p-4 bg-muted/50 hover:bg-muted transition-colors">
                            <div className="flex items-center gap-3">
                              <FolderOpen className="h-5 w-5 text-primary" />
                              <span className="font-semibold text-lg">{section}</span>
                              <Badge variant="secondary" className="ml-2">
                                {groupedQuestions[section].length} questions
                              </Badge>
                            </div>
                            {expandedSections[section] !== false ? (
                              <ChevronDown className="h-5 w-5 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-5 w-5 text-muted-foreground" />
                            )}
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="p-4 space-y-3">
                            {sectionLabels[section] && Object.values(sectionLabels[section]).some(v => v) && (
                              <div className="flex flex-wrap gap-2 pb-3 border-b border-dashed">
                                <span className="text-xs font-medium text-muted-foreground mr-1">Scale:</span>
                                {[1,2,3,4,5].map(n => (
                                  sectionLabels[section][String(n)] ? (
                                    <span key={n} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                                      {n} = {sectionLabels[section][String(n)]}
                                    </span>
                                  ) : null
                                ))}
                              </div>
                            )}
                            {groupedQuestions[section].map((question, index) => {
                              const globalIndex = selectedSurvey.questions.findIndex(q => q.id === question.id);
                              return (
                                <div 
                                  key={question.id}
                                  className="flex items-start gap-4 p-4 bg-background border rounded-lg"
                                  data-testid={`question-${globalIndex}`}
                                >
                                  <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary/10 text-primary text-sm font-bold">
                                    {globalIndex + 1}
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      {getQuestionTypeIcon(question.type)}
                                      <Badge variant="outline" className="text-xs capitalize">
                                        {question.type.replace('_', ' ')}
                                      </Badge>
                                      {question.is_required && (
                                        <Badge variant="destructive" className="text-xs">Required</Badge>
                                      )}
                                    </div>
                                    <p className="font-medium">{question.text}</p>
                                    {question.type === 'multi_select' && (question.min_selections || question.max_selections) && (
                                      <p className="text-xs text-muted-foreground mt-1">
                                        Selections: {question.min_selections || 0} – {question.max_selections || 'unlimited'}
                                      </p>
                                    )}
                                    {question.options && question.options.length > 0 && (
                                      <div className="mt-2 flex flex-wrap gap-2">
                                        {question.options.map((opt, i) => (
                                          <span key={i} className="text-xs bg-muted px-2 py-1 rounded">
                                            {opt.text}{question.type === 'multi_select' && opt.score ? ` (${opt.score})` : ''}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  {selectedSurvey.response_count === 0 && (
                                    <div className="flex items-center gap-2">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleEditQuestion(question)}
                                      >
                                        <Edit2 className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleDeleteQuestion(question.id)}
                                        className="text-destructive hover:text-destructive"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    ));
                  })()
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          // Survey List View
          <div>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold mb-2">Survey Builder</h1>
                <p className="text-muted-foreground">Create and manage employee motivation surveys</p>
              </div>
              {!isReadOnly && (
                <Button onClick={handleCreateSurvey} data-testid="create-survey-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Survey
                </Button>
              )}
            </div>

            {surveys.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <ListChecks className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading text-lg font-medium mb-2">No surveys yet</h3>
                  <p className="text-muted-foreground mb-4">{isReadOnly ? 'No surveys available' : 'Create your first survey to get started'}</p>
                  {!isReadOnly && <Button onClick={handleCreateSurvey}>Create Survey</Button>}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {surveys.map((survey) => {
                  const status = survey.status || (survey.is_active ? 'active' : 'inactive');
                  const isDeleted = status === 'deleted';
                  const isInactive = status === 'inactive';
                  
                  return (
                  <Card 
                    key={survey.id} 
                    className={`hover-lift cursor-pointer ${isDeleted ? 'opacity-60 border-destructive/30' : isInactive ? 'opacity-75 border-yellow-500/30' : ''}`}
                    data-testid={`survey-card-${survey.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-heading font-semibold text-lg line-clamp-1">
                              {survey.title}
                            </h3>
                            <Badge variant={isDeleted ? 'destructive' : isInactive ? 'secondary' : 'default'} className="text-[10px] shrink-0">
                              {isDeleted ? 'Deleted' : isInactive ? 'Inactive' : 'Active'}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {survey.description}
                          </p>
                        </div>
                        {!isReadOnly && !isDeleted && (
                          <Switch
                            checked={survey.is_active}
                            onCheckedChange={() => handleToggleActive(survey)}
                            data-testid={`toggle-${survey.id}`}
                          />
                        )}
                      </div>

                      <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(survey.end_date), 'MMM dd')}
                        </span>
                        <span className="flex items-center gap-1">
                          <ListChecks className="h-3 w-3" />
                          {survey.questions?.length || 0} Q
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="h-3 w-3" />
                          {survey.response_count || 0} responses
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => handleViewSurvey(survey)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View
                        </Button>
                        {!isReadOnly && !isDeleted && (
                          <>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleEditSurvey(survey)}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleDeleteSurvey(survey.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {isSuperAdmin && isDeleted && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleReactivateSurvey(survey.id)}
                            className="text-green-600 hover:text-green-700"
                          >
                            <ToggleRight className="h-4 w-4 mr-1" />
                            Restore
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Survey Dialog */}
        <Dialog open={showSurveyDialog} onOpenChange={setShowSurveyDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingSurvey ? 'Edit Survey' : 'Create New Survey'}
              </DialogTitle>
              <DialogDescription>
                {editingSurvey ? 'Update survey details' : 'Fill in the details to create a new survey'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Survey Title</Label>
                <Input
                  id="title"
                  value={surveyForm.title}
                  onChange={(e) => setSurveyForm({ ...surveyForm, title: e.target.value })}
                  placeholder="Employee Motivation Survey - Q1 2025"
                  data-testid="survey-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={surveyForm.description}
                  onChange={(e) => setSurveyForm({ ...surveyForm, description: e.target.value })}
                  placeholder="Please share your feedback to help us improve..."
                  data-testid="survey-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <Calendar className="h-4 w-4 mr-2" />
                        {format(surveyForm.start_date, 'MMM dd, yyyy')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarComponent
                        mode="single"
                        selected={surveyForm.start_date}
                        onSelect={(date) => date && setSurveyForm({ ...surveyForm, start_date: date })}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <Calendar className="h-4 w-4 mr-2" />
                        {format(surveyForm.end_date, 'MMM dd, yyyy')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarComponent
                        mode="single"
                        selected={surveyForm.end_date}
                        onSelect={(date) => date && setSurveyForm({ ...surveyForm, end_date: date })}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                <div>
                  <p className="font-medium">Anonymous Responses</p>
                  <p className="text-xs text-muted-foreground">Employee identities will be hidden in reports</p>
                </div>
                <Switch
                  checked={surveyForm.is_anonymous}
                  onCheckedChange={(checked) => setSurveyForm({ ...surveyForm, is_anonymous: checked })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSurveyDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveSurvey} data-testid="save-survey-btn">
                <Save className="h-4 w-4 mr-2" />
                {editingSurvey ? 'Update' : 'Create'} Survey
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Question Dialog */}
        <Dialog open={showQuestionDialog} onOpenChange={setShowQuestionDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingQuestion ? 'Edit Question' : 'Add Question'}
              </DialogTitle>
              <DialogDescription>
                Configure the question settings
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Section Selection - Manual Input */}
              <div className="space-y-2">
                <Label>Section</Label>
                <div className="flex gap-2">
                  <Select 
                    value={questionForm.section}
                    onValueChange={(value) => setQuestionForm({ ...questionForm, section: value })}
                  >
                    <SelectTrigger data-testid="question-section-select" className="flex-1">
                      <SelectValue placeholder="Select or create a section" />
                    </SelectTrigger>
                    <SelectContent>
                      {surveySections.map((section) => (
                        <SelectItem key={section} value={section}>
                          <div className="flex items-center gap-2">
                            <FolderOpen className="h-4 w-4" /> {section}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Add New Section */}
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder="Type new section name..."
                    value={newSectionName}
                    onChange={(e) => setNewSectionName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddSection();
                      }
                    }}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddSection}
                    disabled={!newSectionName.trim()}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Section
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Create sections to organize questions (e.g., "Work Environment", "Management")
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="question-text">Question</Label>
                <Textarea
                  id="question-text"
                  value={questionForm.text}
                  onChange={(e) => setQuestionForm({ ...questionForm, text: e.target.value })}
                  placeholder="Enter your question here..."
                  data-testid="question-text-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Question Type</Label>
                <Select 
                  value={questionForm.type}
                  onValueChange={(value) => setQuestionForm({ ...questionForm, type: value, options: [] })}
                >
                  <SelectTrigger data-testid="question-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rating">
                      <div className="flex items-center gap-2">
                        <Star className="h-4 w-4" /> Rating (1-5)
                      </div>
                    </SelectItem>
                    <SelectItem value="yes_no">
                      <div className="flex items-center gap-2">
                        <ToggleLeft className="h-4 w-4" /> Yes / No
                      </div>
                    </SelectItem>
                    <SelectItem value="multiple_choice">
                      <div className="flex items-center gap-2">
                        <ListChecks className="h-4 w-4" /> Multiple Choice (single answer)
                      </div>
                    </SelectItem>
                    <SelectItem value="multi_select">
                      <div className="flex items-center gap-2">
                        <CheckSquare className="h-4 w-4" /> Multi-Select (choose multiple)
                      </div>
                    </SelectItem>
                    <SelectItem value="open_ended">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4" /> Open Ended
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {questionForm.type === 'multiple_choice' && (
                <div className="space-y-2">
                  <Label>Options</Label>
                  <div className="space-y-2">
                    {questionForm.options.map((opt, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          value={opt.text}
                          onChange={(e) => updateOption(index, e.target.value)}
                          placeholder={`Option ${index + 1}`}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeOption(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={addOption}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Option
                    </Button>
                  </div>
                </div>
              )}

              {questionForm.type === 'multi_select' && (
                <div className="space-y-3" data-testid="multi-select-config">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Min Selections</Label>
                      <Input
                        type="number"
                        min="0"
                        value={questionForm.min_selections ?? ''}
                        onChange={(e) => setQuestionForm({ ...questionForm, min_selections: e.target.value === '' ? null : Number(e.target.value) })}
                        placeholder="e.g. 1"
                        data-testid="min-selections-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Max Selections (e.g., "Choose 3")</Label>
                      <Input
                        type="number"
                        min="1"
                        value={questionForm.max_selections ?? ''}
                        onChange={(e) => setQuestionForm({ ...questionForm, max_selections: e.target.value === '' ? null : Number(e.target.value) })}
                        placeholder="e.g. 3"
                        data-testid="max-selections-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Options <span className="text-xs text-muted-foreground">(score 1-5 used for analytics)</span></Label>
                    <div className="space-y-2">
                      {questionForm.options.map((opt, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Input
                            value={opt.text}
                            onChange={(e) => updateOption(index, e.target.value)}
                            placeholder={`Option ${index + 1}`}
                            className="flex-1"
                            data-testid={`multi-option-text-${index}`}
                          />
                          <Input
                            type="number"
                            min="0"
                            max="5"
                            value={opt.score ?? 0}
                            onChange={(e) => updateOptionScore(index, e.target.value)}
                            placeholder="Score"
                            className="w-20"
                            data-testid={`multi-option-score-${index}`}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeOption(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <Button variant="outline" size="sm" onClick={addOption} data-testid="add-multi-option-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Option
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                <div>
                  <p className="font-medium">Required</p>
                  <p className="text-xs text-muted-foreground">Employees must answer this question</p>
                </div>
                <Switch
                  checked={questionForm.is_required}
                  onCheckedChange={(checked) => setQuestionForm({ ...questionForm, is_required: checked })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowQuestionDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveQuestion} data-testid="save-question-btn">
                <Save className="h-4 w-4 mr-2" />
                {editingQuestion ? 'Update' : 'Add'} Question
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Section Manager Dialog (Super Admin Only) */}
        <Dialog open={showSectionManager} onOpenChange={setShowSectionManager}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Manage Sections</DialogTitle>
              <DialogDescription>
                Add sections and define custom rating scale labels for each section.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Add New Section */}
              <div className="flex gap-2">
                <Input
                  placeholder="New section name..."
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddSection();
                    }
                  }}
                />
                <Button onClick={handleAddSection} disabled={!newSectionName.trim()}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>

              {/* Composite Section Groups Panel */}
              {surveySections.length > 1 && (
                <div className="border rounded-lg p-3 bg-blue-50/40 space-y-3" data-testid="section-groups-panel">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold">Composite Score Groups</p>
                      <p className="text-xs text-muted-foreground">Group sections to compute combined mean scores (e.g., &quot;Basic Needs&quot; = Section 1 + 2). Honors section weights if set.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={addGroup} data-testid="add-group-btn">
                        <Plus className="h-3 w-3 mr-1" />
                        Add Group
                      </Button>
                      <Button size="sm" onClick={handleSaveSectionGroups} data-testid="save-section-groups">
                        <Save className="h-3 w-3 mr-1" />
                        Save Groups
                      </Button>
                    </div>
                  </div>
                  {sectionGroups.length === 0 ? (
                    <p className="text-xs italic text-muted-foreground">No composite groups yet. Click &quot;Add Group&quot; to combine sections.</p>
                  ) : (
                    <div className="space-y-3">
                      {sectionGroups.map((g, gi) => (
                        <div key={gi} className="border bg-background rounded p-2 space-y-2" data-testid={`group-${gi}`}>
                          <div className="flex items-center gap-2">
                            <Input
                              value={g.name || ''}
                              onChange={(e) => updateGroupName(gi, e.target.value)}
                              placeholder="Group name (e.g., Basic Needs)"
                              className="h-8 text-sm flex-1"
                              data-testid={`group-name-${gi}`}
                            />
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeGroup(gi)}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {surveySections.map((sec) => {
                              const checked = (g.sections || []).includes(sec);
                              return (
                                <button
                                  key={`${gi}-${sec}`}
                                  type="button"
                                  onClick={() => toggleGroupSection(gi, sec)}
                                  className={`text-xs px-2 py-1 rounded-full border transition ${
                                    checked
                                      ? 'bg-primary text-primary-foreground border-primary'
                                      : 'bg-muted/30 text-muted-foreground border-muted hover:bg-muted'
                                  }`}
                                  data-testid={`group-${gi}-section-${sec}`}
                                >
                                  {checked && <Check className="h-3 w-3 inline mr-1" />}
                                  {sec}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Section Weights Panel */}
              {surveySections.length > 1 && (
                <div className="border rounded-lg p-3 bg-muted/30 space-y-3" data-testid="section-weights-panel">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold">Section Weightage</p>
                      <p className="text-xs text-muted-foreground">Higher weight = more impact on overall score. Leave blank or 0 for equal weighting.</p>
                    </div>
                    <Button size="sm" onClick={handleSaveSectionWeights} data-testid="save-section-weights">
                      <Save className="h-3 w-3 mr-1" />
                      Save Weights
                    </Button>
                  </div>
                  {(() => {
                    const total = Object.values(sectionWeights).reduce((s, v) => s + (Number(v) || 0), 0);
                    return total > 0 ? (
                      <p className="text-xs text-muted-foreground">Total: <strong className="text-foreground">{total}</strong> (weights are normalized automatically)</p>
                    ) : null;
                  })()}
                  <div className="grid grid-cols-1 gap-2">
                    {surveySections.map((section) => (
                      <div key={`w-${section}`} className="flex items-center gap-2">
                        <FolderOpen className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="text-sm flex-1 truncate">{section}</span>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={sectionWeights[section] ?? ''}
                          onChange={(e) => setSectionWeights({ ...sectionWeights, [section]: e.target.value })}
                          placeholder="0"
                          className="w-24 h-8 text-sm"
                          data-testid={`weight-${section}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sections List with Labels */}
              <div className="space-y-3">
                {surveySections.map((section) => {
                  const questionCount = (selectedSurvey?.questions || []).filter(q => q.section === section).length;
                  const labels = sectionLabels[section];
                  const isEditing = editingLabelsFor === section;
                  
                  return (
                    <div key={section} className="border rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between p-3 bg-muted/50">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <FolderOpen className="h-4 w-4 text-primary shrink-0" />
                          {editingSectionName === section ? (
                            <div className="flex items-center gap-2 flex-1">
                              <Input
                                value={editedSectionText}
                                onChange={(e) => setEditedSectionText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleRenameSection(section, editedSectionText);
                                  if (e.key === 'Escape') setEditingSectionName(null);
                                }}
                                className="h-8 text-sm"
                                autoFocus
                                data-testid={`rename-input-${section}`}
                              />
                              <Button size="sm" variant="ghost" className="h-8 px-2 text-green-600" onClick={() => handleRenameSection(section, editedSectionText)}>
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setEditingSectionName(null)}>
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <span className="font-medium truncate">{section}</span>
                              <Badge variant="secondary" className="text-xs shrink-0">
                                {questionCount} questions
                              </Badge>
                            </>
                          )}
                        </div>
                        {editingSectionName !== section && (
                          <div className="flex items-center gap-1 shrink-0">
                            {section !== 'General' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setEditingSectionName(section); setEditedSectionText(section); }}
                                className="text-muted-foreground hover:text-foreground"
                                data-testid={`rename-section-${section}`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingLabelsFor(isEditing ? null : section)}
                              className="text-primary"
                              data-testid={`edit-labels-${section}`}
                            >
                              <Star className="h-4 w-4 mr-1" />
                              {labels ? 'Edit Scale' : 'Set Scale'}
                            </Button>
                            {section !== 'General' && isSuperAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteSection(section)}
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                data-testid={`delete-section-${section}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Show current labels */}
                      {labels && !isEditing && (
                        <div className="px-3 py-2 bg-background border-t">
                          <div className="flex flex-wrap gap-2">
                            {[1,2,3,4,5].map(n => (
                              <span key={n} className="text-xs bg-muted px-2 py-1 rounded">
                                <strong>{n}</strong> = {labels[String(n)] || n}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Edit labels form */}
                      {isEditing && (
                        <div className="p-3 bg-background border-t space-y-3">
                          {/* Presets */}
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-2">Quick Presets:</p>
                            <div className="flex flex-wrap gap-2">
                              {DEFAULT_SCALE_PRESETS.map(preset => (
                                <Button
                                  key={preset.name}
                                  variant="outline"
                                  size="sm"
                                  className="text-xs h-7"
                                  onClick={() => handleApplyPreset(section, preset)}
                                  data-testid={`preset-${preset.name.toLowerCase()}`}
                                >
                                  {preset.name}
                                </Button>
                              ))}
                            </div>
                          </div>

                          {/* Custom labels */}
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-muted-foreground">Custom Labels:</p>
                            {[1,2,3,4,5].map(n => (
                              <div key={n} className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">
                                  {n}
                                </div>
                                <Input
                                  value={(sectionLabels[section] || {})[String(n)] || ''}
                                  onChange={(e) => {
                                    const updated = { ...(sectionLabels[section] || {}), [String(n)]: e.target.value };
                                    setSectionLabels({ ...sectionLabels, [section]: updated });
                                  }}
                                  placeholder={`Label for ${n}...`}
                                  className="h-8 text-sm"
                                  data-testid={`label-${section}-${n}`}
                                />
                              </div>
                            ))}
                          </div>

                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleSaveSectionLabels(section, sectionLabels[section] || {})} data-testid={`save-labels-${section}`}>
                              <Save className="h-3 w-3 mr-1" /> Save Labels
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingLabelsFor(null)}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              <p className="text-xs text-muted-foreground">
                Rating scale labels will be shown to employees when answering rating questions in each section.
              </p>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSectionManager(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Clear All Responses Dialog (Super Admin Only) */}
        <Dialog open={showClearResponsesDialog} onOpenChange={(open) => { setShowClearResponsesDialog(open); if (!open) setClearConfirmText(''); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                Clear All Responses
              </DialogTitle>
              <DialogDescription>
                This will permanently delete all <strong>{selectedSurvey?.response_count || 0}</strong> response(s) for this survey. The survey, questions, and section labels will remain intact.
                <br /><br />
                <span className="text-destructive font-medium">This action cannot be undone.</span>
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2 py-2">
              <Label htmlFor="clear-confirm" className="text-sm">
                Type the survey title to confirm: <span className="font-mono font-semibold text-foreground">{selectedSurvey?.title}</span>
              </Label>
              <Input
                id="clear-confirm"
                value={clearConfirmText}
                onChange={(e) => setClearConfirmText(e.target.value)}
                placeholder="Type survey title here..."
                data-testid="clear-confirm-input"
                autoComplete="off"
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowClearResponsesDialog(false); setClearConfirmText(''); }}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleClearAllResponses}
                disabled={clearingResponses || clearConfirmText.trim() !== selectedSurvey?.title}
                data-testid="confirm-clear-responses-btn"
              >
                <Eraser className="h-4 w-4 mr-2" />
                {clearingResponses ? 'Clearing...' : 'Clear All Responses'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default SurveyBuilder;
